import { Box, Flex, Text } from "@chakra-ui/layout";
import { formatCurrency } from "@brazilian-utils/brazilian-utils";
import Calendar, { CalendarEventInput } from "components/Calendar";
import Container from "components/Container";
import { useLoading } from "components/GlobalLoading";
import SelectContaBancaria from "components/Input/SelectContaBancaria";
import Heading from "components/text/heading";
import { useCurrentTenant } from "core/features/auth/tenant.store";
import useGetContasBancarias from "features/contas_bancarias/hooks/useGetContasBancarias";
import useRepassesDiarios from "features/repasse/hooks/useRepassesDiarios";
import {
  RepasseDiarioItemResponse,
  RepasseDiarioStatus,
  RepasseListQuery,
} from "features/repasse/typings";
import colors from "core/resources/theme/colors";
import { EventClickArg, EventContentArg } from "@fullcalendar/react";
import moment from "moment";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ModalTransacoesRepasseDia from "./ModalTransacoesRepasseDia";

function repasseDeveAparecerNoCalendario(
  repasse: RepasseDiarioItemResponse
): boolean {
  if (repasse.valor <= 0) {
    return false;
  }
  return repasse.status === "depositado" || repasse.status === "previsto";
}

function repasseStatusBackground(status: RepasseDiarioStatus): string {
  if (status === "depositado") {
    return colors.green500;
  }
  if (status === "previsto") {
    return colors.yellow500;
  }
  return colors.blueishGray500;
}

function resumoRepasseNoDia(
  lista: RepasseDiarioItemResponse[],
  dataIso: string
): RepasseDiarioItemResponse | null {
  const noDia = lista
    .filter(repasseDeveAparecerNoCalendario)
    .filter((item) => item.data === dataIso);
  if (!noDia.length) {
    return null;
  }
  if (noDia.length === 1) {
    return noDia[0];
  }
  return {
    data: dataIso,
    valor: noDia.reduce((acc, item) => acc + item.valor, 0),
    status: noDia.some((item) => item.status === "depositado")
      ? "depositado"
      : noDia.some((item) => item.status === "previsto")
      ? "previsto"
      : null,
  };
}

type GradeCalendarioRepasse = {
  mes: number;
  ano: number;
  dataInicioGrade: string;
  dataFimGrade: string;
};

type RepasseCalendarEvent = CalendarEventInput & {
  allDay: boolean;
  extendedProps: { repasse: RepasseDiarioItemResponse };
};

type PainelCalendarioRepasseProps = {
  eventos: RepasseCalendarEvent[];
  viewLocal: string;
  setViewLocal: (view: string) => void;
  handleEventClick: (arg: EventClickArg) => void;
  onChangeDate: (dateStart: string, dateEnd: string) => void;
  handleDateClick: (dateStr: string) => void;
  loadingCalendario: boolean;
};

function AvisoSemContasRepasse() {
  return (
    <Box
      role="status"
      mb="16px"
      py={3}
      px={4}
      borderRadius="md"
      bg={colors.blue100}
      borderLeft="4px solid"
      borderLeftColor={colors.blue500}
    >
      <Text fontSize="sm" color={colors.black900}>
        Não há contas bancárias com pagamento online ativas para este condomínio.
        O calendário de repasses depende de ao menos uma conta (tenantContaIds).
      </Text>
    </Box>
  );
}

function PainelCalendarioRepasse({
  eventos,
  viewLocal,
  setViewLocal,
  handleEventClick,
  onChangeDate,
  handleDateClick,
  loadingCalendario,
}: PainelCalendarioRepasseProps) {
  return (
    <Box
      flex={1}
      minH={0}
      w="100%"
      display="flex"
      flexDirection="column"
      bg={colors.white}
      rounded="16px"
      p={2}
      boxShadow="0 0 16px 3px rgba(0, 0, 0, 0.05)"
    >
      <Calendar
        height="100%"
        contentHeight="auto"
        events={eventos}
        setLocalView={setViewLocal}
        handleEventClick={handleEventClick}
        onChangeDate={onChangeDate}
        dateClick={handleDateClick}
        hideOptionsView={false}
        loading={loadingCalendario}
        displayEventTime={false}
        renderEventContent={(info) =>
          renderRepasseEventContent(info, viewLocal)
        }
      />
    </Box>
  );
}

function ConteudoCalendarioDepositos() {
  const { tenant } = useCurrentTenant();
  const {
    data: contasPaginadas,
    isLoading: isLoadingContas,
    isFetching: isFetchingContas,
  } = useGetContasBancarias({ incluirInativas: false });

  const tenantContaIdsSafe2Pay = useMemo(() => {
    return (contasPaginadas?.items ?? [])
      .filter((conta) => conta.ativarPagamentoOnline && conta.ativo)
      .map((conta) => conta.id);
  }, [contasPaginadas?.items]);

  const idsContasExcluidasDoFiltroRepasse = useMemo(
    () =>
      (contasPaginadas?.items ?? [])
        .filter((conta) => !(conta.ativarPagamentoOnline && conta.ativo))
        .map((conta) => conta.id),
    [contasPaginadas?.items]
  );

  const [contasSelecionadasIds, setContasSelecionadasIds] = useState<string[]>(
    []
  );
  const selecaoContasInicializada = useRef(false);

  useEffect(() => {
    selecaoContasInicializada.current = false;
    setContasSelecionadasIds([]);
  }, [tenant.id]);

  useEffect(() => {
    if (selecaoContasInicializada.current) {
      return;
    }
    if (tenantContaIdsSafe2Pay.length === 0) {
      return;
    }
    setContasSelecionadasIds([...tenantContaIdsSafe2Pay]);
    selecaoContasInicializada.current = true;
  }, [tenantContaIdsSafe2Pay]);

  const tenantContaIdsParaApi = useMemo(
    () =>
      contasSelecionadasIds.filter((id) =>
        tenantContaIdsSafe2Pay.includes(id)
      ),
    [contasSelecionadasIds, tenantContaIdsSafe2Pay]
  );

  const [gradeCalendario, setGradeCalendario] =
    useState<GradeCalendarioRepasse | null>(null);
  const [modalAberto, setModalAberto] = useState(false);
  const [dataModal, setDataModal] = useState<string | null>(null);
  const [repasseModal, setRepasseModal] =
    useState<RepasseDiarioItemResponse | null>(null);
  const [viewLocal, setViewLocal] = useState("dayGridMonth");

  const repasseListQuery: RepasseListQuery | null = useMemo(() => {
    if (!gradeCalendario || tenantContaIdsParaApi.length === 0) {
      return null;
    }
    return {
      mes: gradeCalendario.mes,
      ano: gradeCalendario.ano,
      tenantContaIds: tenantContaIdsParaApi,
    };
  }, [gradeCalendario, tenantContaIdsParaApi]);

  const {
    data: repasses = [],
    isLoading: isLoadingRepasses,
    isFetching: isFetchingRepasses,
  } = useRepassesDiarios(repasseListQuery);

  const carregandoTela =
    isLoadingContas ||
    isFetchingContas ||
    Boolean(
      repasseListQuery && (isLoadingRepasses || isFetchingRepasses)
    );

  useLoading(carregandoTela, "Carregando calendário...");

  const onChangeDate = useCallback((dateStart: string, dateEnd: string) => {
    const inicioGrade = moment(dateStart, "YYYY/MM/DD");
    const fimGrade = moment(dateEnd, "YYYY/MM/DD");
    const diasVisiveis = Math.max(1, fimGrade.diff(inicioGrade, "days"));
    const referenciaMes = inicioGrade
      .clone()
      .add(Math.floor(diasVisiveis / 2), "days");

    setGradeCalendario({
      mes: referenciaMes.month() + 1,
      ano: referenciaMes.year(),
      dataInicioGrade: inicioGrade.format("YYYY-MM-DD"),
      dataFimGrade: fimGrade.format("YYYY-MM-DD"),
    });
  }, []);

  const eventos: RepasseCalendarEvent[] = useMemo(() => {
    const filtrados =
      gradeCalendario == null
        ? repasses
        : repasses.filter(
            (repasse) =>
              repasse.data >= gradeCalendario.dataInicioGrade &&
              repasse.data <= gradeCalendario.dataFimGrade
          );

    return filtrados
      .filter(repasseDeveAparecerNoCalendario)
      .map((repasse, index) => ({
        id: `${repasse.data}-${index}`,
        title: `R$ ${formatCurrency(repasse.valor)}`,
        start: repasse.data,
        allDay: true,
        backgroundColor: repasseStatusBackground(repasse.status),
        extendedProps: { repasse },
      }));
  }, [repasses, gradeCalendario]);

  const abrirModalDia = useCallback(
    (dataIso: string) => {
      const resumo = resumoRepasseNoDia(repasses, dataIso);
      if (!resumo) {
        return;
      }
      setDataModal(dataIso);
      setRepasseModal(resumo);
      setModalAberto(true);
    },
    [repasses]
  );

  const handleEventClick = useCallback(
    (arg: EventClickArg) => {
      const repasse = arg.event.extendedProps.repasse as
        | RepasseDiarioItemResponse
        | undefined;
      if (!repasse) {
        return;
      }
      setDataModal(repasse.data);
      setRepasseModal(resumoRepasseNoDia(repasses, repasse.data));
      setModalAberto(true);
    },
    [repasses]
  );

  const handleDateClick = useCallback(
    (dateStr: string) => {
      abrirModalDia(dateStr);
    },
    [abrirModalDia]
  );

  const fecharModal = useCallback(() => {
    setModalAberto(false);
    setDataModal(null);
    setRepasseModal(null);
  }, []);

  return (
    <>
      <Container maxW="100%" px={{ base: 4, md: 6 }}>
        <Flex
          direction="column"
          alignItems="center"
          w="100%"
          minH="calc(100vh - 220px)"
        >
          <Box
            w="100%"
            maxW="1280px"
            mx="auto"
            alignSelf="stretch"
            display="flex"
            flexDirection="column"
            flex={1}
            minH={0}
          >
            {tenantContaIdsSafe2Pay.length === 0 ? (
              <AvisoSemContasRepasse />
            ) : null}
            <Flex
              w="100%"
              mb="16px"
              justifyContent="space-between"
              alignItems="center"
              gap={4}
              flexWrap="wrap"
            >
              <Heading flexShrink={0}>Calendário de depósitos</Heading>
              {tenantContaIdsSafe2Pay.length > 0 ? (
                <Box
                  minW={{ base: "100%", sm: "260px" }}
                  maxW="440px"
                  w={{ base: "100%", md: "auto" }}
                >
                  <SelectContaBancaria
                    mode="multiple"
                    allowClear
                    incluirInativas={false}
                    placeholder="Selecione uma ou mais contas"
                    value={contasSelecionadasIds}
                    onChange={(ids: string[]) =>
                      setContasSelecionadasIds(ids ?? [])
                    }
                    filteredIds={idsContasExcluidasDoFiltroRepasse}
                    style={{ width: "100%" }}
                  />
                  {tenantContaIdsParaApi.length === 0 ? (
                    <Text mt={2} fontSize="sm" color={colors.red} textAlign="right">
                      Selecione ao menos uma conta para carregar os repasses.
                    </Text>
                  ) : null}
                </Box>
              ) : null}
            </Flex>
            <Flex direction="column" flex={1} w="100%" minH={0}>
              <PainelCalendarioRepasse
                eventos={eventos}
                viewLocal={viewLocal}
                setViewLocal={setViewLocal}
                handleEventClick={handleEventClick}
                onChangeDate={onChangeDate}
                handleDateClick={handleDateClick}
                loadingCalendario={
                  Boolean(repasseListQuery) &&
                  (isLoadingRepasses || isFetchingRepasses)
                }
              />
            </Flex>
          </Box>
        </Flex>
      </Container>
      <ModalTransacoesRepasseDia
        isOpen={modalAberto}
        onClose={fecharModal}
        dataIso={dataModal}
        repasseDia={repasseModal}
        contasBancariasIds={tenantContaIdsParaApi}
      />
    </>
  );
}

function renderRepasseEventContent(
  eventInfo: EventContentArg,
  viewType: string
) {
  const fundo =
    typeof eventInfo.event.backgroundColor === "string"
      ? eventInfo.event.backgroundColor
      : colors.blueishGray500;

  return (
    <Box
      title={String(eventInfo.event.title)}
      rounded="md"
      px={2}
      py={0.5}
      color={colors.white}
      bg={fundo}
      cursor="pointer"
      display="flex"
      alignItems="center"
      minH="24px"
      maxW="100%"
      fontSize={viewType === "dayGridMonth" ? "12px" : "13px"}
      fontWeight="500"
      lineHeight="1.25"
      overflow="hidden"
      css={{
        display: "-webkit-box",
        WebkitLineClamp: 2,
        WebkitBoxOrient: "vertical",
      }}
    >
      {String(eventInfo.event.title)}
    </Box>
  );
}

export default ConteudoCalendarioDepositos;
